/**
 * @file fields.tsx
 *
 * @fileoverview Helpers for field manipulation for entity forms.
 */

import { FieldGroupInfo } from '~/jobiqo/models/forms';
import { EntityField, EntityDefinition } from '~/jobiqo/models/entity';
import { excludedFields } from './excludedFields';
import { getGroupsFieldKeys } from './fieldGroups';

/**
 * Filters the list of fields.
 *
 * Will check both generally disallowed fields but also any fields
 * passed to the excludedFields prop.
 *
 * @param {EntityField[]} fields
 *   The unfiltered list of fields.
 *
 * @returns {EntityField[]}
 *   List of fields after removing unwanted fields.
 */
export const filterExcludedFields = (
  fieldGroups: FieldGroupInfo[],
  excludedFieldList: string[],
  fields: EntityField[],
  definition: EntityDefinition,
): EntityField[] => {
  return fields.filter(
    (field) =>
      !excludedFieldList.includes(field.id) &&
      !excludedFields.includes(field.id) &&
      getGroupsFieldKeys(fieldGroups, definition, excludedFieldList).includes(
        field.id,
      ),
  );
};

/**
 * Process fields before they are sent to our backend.
 *
 * Converts fields to the format expected by Drupal.
 * e.g. term references with {targetId: 'value'}
 *
 * @param {{}} values
 *   The values passed through the form.
 *
 * @return {{}}
 *   The transformed values.
 */
export const processFields = (
  fieldGroups: FieldGroupInfo[],
  definition: EntityDefinition,
  excludedFieldList: string[],
  values: {},
  additionalFieldList: string[],
): {} => {
  const newFields = {};
  filterExcludedFields(
    fieldGroups,
    excludedFieldList,
    definition.fields,
    definition,
  ).forEach((field) => {
    // Transform entity reference fields.
    if (field.type === 'entity_reference' && values[field.id]) {
      newFields[field.id] =
        field.maxNumItems === 1
          ? parseInt(values[field.id].tid, 10)
          : values[field.id].map((i) => parseInt(i.tid, 10));

      const targetBundles = field?.settings?.filter(
        (i) => i.key === 'handler_settings',
      )?.[0]?.value?.target_bundles;
      // Check if targetBundles has key with word free_tagging in it.
      const hasFreeTagging = Object.keys(targetBundles).some((key) => {
        return key.includes('free_tagging');
      });

      // Handle taxonomy reference fields that have free terms.
      if (hasFreeTagging) {
        newFields[`${field.id}_free`] = values[`${field.id}_free`];
      }

      return;
    }

    // Term level free items are special
    // don't have a "field".
    if (field.type === 'term_level' && values[`${field.id}_free`]) {
      newFields[field.id] = values[field.id];
      newFields[`${field.id}_free`] = values[`${field.id}_free`];
      return;
    }

    // IF values are not an actual bool try to transform them to a boolean. (either 1 or true as string)
    if (field.type === 'boolean' && typeof values[field.id] !== 'boolean') {
      newFields[field.id] =
        values[field.id] === '1' ||
        values[field.id] === 1 ||
        values[field.id] === 'true';
      return;
    }

    // By default just sent the value as is.
    newFields[field.id] = values[field.id];
  });

  // Include additional fields in newFields.
  additionalFieldList.forEach((fieldName) => {
    if (values[fieldName] !== undefined && values[fieldName] !== null) {
      newFields[fieldName] = values[fieldName];
    }
  });

  return newFields;
};
